import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { errorBookApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, BookX, CheckCircle2, Trash2, RefreshCw,
  Filter, AlertTriangle, Target, Loader2, ChevronRight, Flame,
} from 'lucide-react';

const SOURCE_LABEL = {
  quick: 'Quick Answer',
  real: 'Real Exam',
  bot: 'Bot Demo',
  dpp: 'Daily Practice',
  mock: 'Mock Test',
  pyq: 'Past Year',
};

const SOURCE_COLOR = {
  quick: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  real: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  bot: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  dpp: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  mock: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  pyq: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

export default function ErrorBook() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [data, setData] = useState({ errors: [], total: 0, unresolved: 0, weak_topics: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ resolved: 'pending', source: 'all', subject: 'all' });
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.resolved === 'pending') params.resolved = false;
      else if (filter.resolved === 'resolved') params.resolved = true;
      if (filter.source !== 'all') params.source = filter.source;
      if (filter.subject !== 'all') params.subject = filter.subject;
      const d = await errorBookApi.list(params);
      setData(d);
    } catch {
      toast.error('Failed to load Error Book');
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (errorId) => {
    await errorBookApi.resolve(errorId);
    toast.success('Marked as resolved');
    load();
  };

  const remove = async (errorId) => {
    await errorBookApi.remove(errorId);
    toast.success('Deleted');
    load();
  };

  const bulkResolveTopic = async (subject, topic) => {
    const res = await errorBookApi.bulkResolve({ subject, topic });
    toast.success(`Resolved ${res.count} entries in ${topic}`);
    load();
  };

  const subjects = Array.from(new Set(data.errors.map(e => e.subject).filter(Boolean)));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="errorbook-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BookX className="w-5 h-5 text-rose-500" />Error Book
              </h1>
              <p className="text-xs text-muted-foreground">Every wrong answer, one place. Fix them, master the syllabus.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="errorbook-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="errorbook-stats">
          <StatCard icon={AlertTriangle} color="rose" label="Total errors logged" value={data.total} testId="stat-total" />
          <StatCard icon={Flame} color="orange" label="Pending to fix" value={data.unresolved} testId="stat-unresolved" />
          <StatCard icon={CheckCircle2} color="emerald" label="Resolved" value={data.total - data.unresolved} testId="stat-resolved" />
        </div>

        {/* Weak topics */}
        {data.weak_topics?.length > 0 && (
          <div className="p-5 rounded-2xl border border-border bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-950/20 dark:to-orange-950/20" data-testid="errorbook-weak-topics">
            <h2 className="font-bold mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-rose-500" />Weak Chapters — Re-study these first
            </h2>
            <div className="flex flex-wrap gap-2">
              {data.weak_topics.map((w, i) => (
                <button
                  key={i}
                  onClick={() => bulkResolveTopic(w.subject, w.topic)}
                  data-testid={`weak-topic-${i}`}
                  className="group px-3 py-2 rounded-lg bg-white dark:bg-card border border-rose-200 dark:border-rose-900 hover:border-rose-400 transition text-left"
                >
                  <p className="text-xs font-semibold">{w.topic}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {w.subject} · <span className="text-rose-500 font-semibold">{w.count}</span> {w.count === 1 ? 'error' : 'errors'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl border border-border bg-card" data-testid="errorbook-filters">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={filter.resolved} onValueChange={(v) => setFilter(f => ({ ...f, resolved: v }))}>
            <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.source} onValueChange={(v) => setFilter(f => ({ ...f, source: v }))}>
            <SelectTrigger className="w-40" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {subjects.length > 0 && (
            <Select value={filter.subject} onValueChange={(v) => setFilter(f => ({ ...f, subject: v }))}>
              <SelectTrigger className="w-40" data-testid="filter-subject"><SelectValue placeholder="Subject" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjects.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="sm" onClick={load} data-testid="filter-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
          </Button>
        </div>

        {/* Error list */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : data.errors.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="errorbook-empty">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="font-semibold">No errors here!</p>
            <p className="text-sm text-muted-foreground">Either you're crushing it, or you haven't practiced yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="errorbook-list">
            {data.errors.map((e, i) => (
              <ErrorCard
                key={e.error_id}
                item={e}
                expanded={expandedId === e.error_id}
                onToggle={() => setExpandedId(x => x === e.error_id ? null : e.error_id)}
                onResolve={() => resolve(e.error_id)}
                onDelete={() => remove(e.error_id)}
                index={i}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, testId }) {
  const colorMap = {
    rose: 'text-rose-500 bg-rose-100 dark:bg-rose-950/40',
    orange: 'text-orange-500 bg-orange-100 dark:bg-orange-950/40',
    emerald: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-950/40',
  };
  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl ${colorMap[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function ErrorCard({ item, expanded, onToggle, onResolve, onDelete, index }) {
  return (
    <div
      className={`rounded-xl border transition-all ${item.resolved ? 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/10' : 'border-border bg-card'}`}
      data-testid={`error-card-${index}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-start gap-3"
        data-testid={`error-toggle-${index}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <Badge className={SOURCE_COLOR[item.source] || 'bg-muted'}>{SOURCE_LABEL[item.source] || item.source}</Badge>
            {item.year && <Badge variant="outline" className="text-[10px]">{item.year}</Badge>}
            <span className="text-[11px] text-muted-foreground">{item.subject} · {item.topic}</span>
            {item.resolved && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Resolved</Badge>}
          </div>
          <p className="text-sm font-medium line-clamp-2">{item.question}</p>
        </div>
        <ChevronRight className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 space-y-3 border-t border-border/60" data-testid={`error-expanded-${index}`}>
          {item.options?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
              {item.options.map((opt, oi) => {
                const isCorrect = opt === item.correct_answer;
                const isUserPick = opt === item.user_answer;
                return (
                  <div
                    key={oi}
                    className={`text-xs px-3 py-2 rounded-lg border ${
                      isCorrect ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800'
                        : isUserPick ? 'border-rose-400 bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-800'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {String.fromCharCode(65 + oi)}. {opt}
                    {isCorrect && <span className="ml-1 font-bold">✓ Correct</span>}
                    {isUserPick && !isCorrect && <span className="ml-1 font-bold">← Your pick</span>}
                  </div>
                );
              })}
            </div>
          )}
          {item.explanation && (
            <div className="p-3 rounded-lg bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-xs text-sky-900 dark:text-sky-200">
              <span className="font-semibold">Why: </span>{item.explanation}
            </div>
          )}
          <div className="flex gap-2">
            {!item.resolved && (
              <Button size="sm" onClick={onResolve} data-testid={`resolve-btn-${index}`} className="bg-emerald-500 hover:bg-emerald-600">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Got it — mark resolved
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onDelete} data-testid={`delete-btn-${index}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
